import {
  ActivityResponse,
  ConsignmentResponse,
  Configuration,
  CustomerOrderResponse,
  HumanMeanResponse,
  InvoicingDocumentResponse,
  ItemResponse,
  LogisticsObjectsActivitiesApi,
  LogisticsObjectsConsignmentsApi,
  LogisticsObjectsCustomerOrdersApi,
  LogisticsObjectsHumanMeansApi,
  LogisticsObjectsInvoicingDocumentsApi,
  LogisticsObjectsItemsApi,
  LogisticsObjectsMaterialMeansApi,
  LogisticsObjectsPiecesApi,
  LogisticsObjectsProductsApi,
  LogisticsObjectsQuotationRequestsApi,
  LogisticsObjectsQuotationsApi,
  LogisticsObjectsServiceRequestsApi,
  LogisticsObjectsServicesApi,
  HostsApi,
  MembersApi,
  LogisticsObjectMetadata,
  MaterialMeanResponse,
  PieceResponse,
  ProductResponse,
  ChangeRequest,
  PaginatedChangeRequests,
  ACL,
  ACLAuthorization,
  PaginatedEvents,
  QuotationRequestResponse,
  QuotationResponse,
  ServiceRequestResponse,
  ServiceResponse,
} from '@/api/bol';
import {
  LOGISTICS_OBJECT_ENTITIES,
  getLogisticsObjectTypeTokens,
  type LogisticsObjectData,
  type LogisticsObjectEntity,
} from '@/types/logisticsObject';
import { oAuthService } from './oAuthService';

type LogisticsObjectsUnknownApi =
  | LogisticsObjectsActivitiesApi
  | LogisticsObjectsConsignmentsApi
  | LogisticsObjectsCustomerOrdersApi
  | LogisticsObjectsHumanMeansApi
  | LogisticsObjectsInvoicingDocumentsApi
  | LogisticsObjectsItemsApi
  | LogisticsObjectsMaterialMeansApi
  | LogisticsObjectsPiecesApi
  | LogisticsObjectsProductsApi
  | LogisticsObjectsQuotationRequestsApi
  | LogisticsObjectsQuotationsApi
  | LogisticsObjectsServiceRequestsApi
  | LogisticsObjectsServicesApi;

type LogisticsObjectResponse =
  | ActivityResponse
  | ConsignmentResponse
  | CustomerOrderResponse
  | HumanMeanResponse
  | InvoicingDocumentResponse
  | ItemResponse
  | MaterialMeanResponse
  | PieceResponse
  | ProductResponse
  | QuotationRequestResponse
  | QuotationResponse
  | ServiceRequestResponse
  | ServiceResponse;

type ApiMethod = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<unknown>;
type ApiMethodReturningLogisticsObjectData = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<LogisticsObjectData | LogisticsObjectResponse>;
type ApiMethodReturningLogisticsObjectMetadata = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<LogisticsObjectMetadata>;
type ApiMethodReturningPaginatedChangeRequests = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<PaginatedChangeRequests>;
type ApiMethodReturningChangeRequest = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<ChangeRequest>;
type ApiMethodReturningChangeRequestOrNullOrUndefined = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<ChangeRequest | null | undefined>;
type ApiMethodReturningACL = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<ACL>;
type ApiMethodReturningACLAuthorization = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<ACLAuthorization>;
type ApiMethodReturningVoid = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<void>;
type ApiMethodReturningPaginatedEvents = (request: Record<string, unknown>, initOverrides?: unknown) => Promise<PaginatedEvents>;

export interface LogisticsObjectMethodAliases {
  getLogisticsObjectById: ApiMethodReturningLogisticsObjectData;
  getLogisticsObjectMetadataById: ApiMethodReturningLogisticsObjectMetadata;
  listChangeRequestsOfLogisticsObjectById: ApiMethodReturningPaginatedChangeRequests;
  getLogisticsObjectACLById: ApiMethodReturningACL;
  getChangeRequestOnLogisticsObjectById: ApiMethodReturningChangeRequest;
  getLogisticsObjectACLAuthorizationByMemberId: ApiMethodReturningACLAuthorization;
  upsertLogisticsObjectACLAuthorizationById: ApiMethodReturningACLAuthorization;
  deleteLogisticsObjectACLAuthorizationByMemberId: ApiMethodReturningVoid;
  createChangeRequestOnLogisticsObjectById: ApiMethodReturningChangeRequestOrNullOrUndefined;
  updateChangeRequestOnLogisticsObjectById: ApiMethodReturningChangeRequest;
  getLogisticsObjectEvents: ApiMethodReturningPaginatedEvents;
  createLogisticsObjectEvent: ApiMethodReturningVoid;
  deleteLogisticsObjectEventById: ApiMethodReturningVoid;
}

export type LogisticsObjectsApi = LogisticsObjectsUnknownApi & LogisticsObjectMethodAliases;
export type QuotationRequestsApi = LogisticsObjectsQuotationRequestsApi & LogisticsObjectMethodAliases;
export type QuotationsApi = LogisticsObjectsQuotationsApi & LogisticsObjectMethodAliases;
export type ServicesApi = LogisticsObjectsServicesApi & LogisticsObjectMethodAliases;
export type ActivitiesApi = LogisticsObjectsActivitiesApi & LogisticsObjectMethodAliases;
export type ConsignmentsApi = LogisticsObjectsConsignmentsApi & LogisticsObjectMethodAliases;
export type CustomerOrdersApi = LogisticsObjectsCustomerOrdersApi & LogisticsObjectMethodAliases;
export type HumanMeansApi = LogisticsObjectsHumanMeansApi & LogisticsObjectMethodAliases;
export type InvoicingDocumentsApi = LogisticsObjectsInvoicingDocumentsApi & LogisticsObjectMethodAliases;
export type ItemsApi = LogisticsObjectsItemsApi & LogisticsObjectMethodAliases;
export type MaterialMeansApi = LogisticsObjectsMaterialMeansApi & LogisticsObjectMethodAliases;
export type PiecesApi = LogisticsObjectsPiecesApi & LogisticsObjectMethodAliases;
export type ProductsApi = LogisticsObjectsProductsApi & LogisticsObjectMethodAliases;
export type ServiceRequestsApi = LogisticsObjectsServiceRequestsApi & LogisticsObjectMethodAliases;

function normalizeLogisticsObjectType(logisticsObjectType: string): LogisticsObjectEntity {
  if ((LOGISTICS_OBJECT_ENTITIES as readonly string[]).includes(logisticsObjectType)) {
    return logisticsObjectType as LogisticsObjectEntity;
  }

  throw new Error(
    `Unsupported logistics object type "${logisticsObjectType}". Expected one of: ${LOGISTICS_OBJECT_ENTITIES.join(', ')}`,
  );
}

function getApi(logisticsObjectType: LogisticsObjectEntity): LogisticsObjectsUnknownApi {
  switch (logisticsObjectType) {
    case 'activity':
      return getActivitiesApi();
    case 'consignment':
      return getConsignmentsApi();
    case 'customer-order':
      return getCustomerOrdersApi();
    case 'human-mean':
      return getHumanMeansApi();
    case 'invoicing-document':
      return getInvoicingDocumentsApi();
    case 'item':
      return getItemsApi();
    case 'material-mean':
      return getMaterialMeansApi();
    case 'piece':
      return getPiecesApi();
    case 'product':
      return getProductsApi();
    case 'quotation-request':
      return getQuotationRequestsApi();
    case 'quotation':
      return getQuotationsApi();
    case 'service-request':
      return getServiceRequestsApi();
    case 'service':
      return getServicesApi();
  }
}

function getApiMethod(api: LogisticsObjectsUnknownApi, methodName: string): ApiMethod {
  const apiRecord = api as unknown as Record<string, unknown>;
  const method = apiRecord[methodName];

  if (typeof method !== 'function') {
    throw new Error(`Method "${methodName}" was not found on logistics object API instance`);
  }

  return method as ApiMethod;
}

function getMethodMap(typeNamePascal: string): Record<keyof LogisticsObjectMethodAliases, string> {
  return {
    getLogisticsObjectById: `get${typeNamePascal}ById`,
    getLogisticsObjectMetadataById: `get${typeNamePascal}MetadataById`,
    listChangeRequestsOfLogisticsObjectById: `listChangeRequestsOf${typeNamePascal}ById`,
    getLogisticsObjectACLById: `get${typeNamePascal}ACLById`,
    getChangeRequestOnLogisticsObjectById: `getChangeRequestOn${typeNamePascal}ById`,
    getLogisticsObjectACLAuthorizationByMemberId: `get${typeNamePascal}ACLAuthorizationByMemberId`,
    upsertLogisticsObjectACLAuthorizationById: `upsert${typeNamePascal}ACLAuthorizationById`,
    deleteLogisticsObjectACLAuthorizationByMemberId: `delete${typeNamePascal}ACLAuthorizationByMemberId`,
    createChangeRequestOnLogisticsObjectById: `createChangeRequestOn${typeNamePascal}ById`,
    updateChangeRequestOnLogisticsObjectById: `updateChangeRequestOn${typeNamePascal}ById`,
    getLogisticsObjectEvents: `get${typeNamePascal}Events`,
    createLogisticsObjectEvent: `create${typeNamePascal}Event`,
    deleteLogisticsObjectEventById: `delete${typeNamePascal}EventById`,
  };
}

class LogisticsObjectApiAliaser<TApi extends LogisticsObjectsUnknownApi> {
  constructor(
    private readonly api: TApi,
    private readonly typeNamePascal: string,
  ) {}

  public build(): TApi & LogisticsObjectMethodAliases {
    const aliasMap = getMethodMap(this.typeNamePascal);
    const apiWithAliases = this.api as unknown as TApi & LogisticsObjectMethodAliases;

    (Object.keys(aliasMap) as Array<keyof LogisticsObjectMethodAliases>).forEach((alias) => {
      switch (alias) {
        case 'getLogisticsObjectById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningLogisticsObjectData;
          break;
        case 'getLogisticsObjectMetadataById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningLogisticsObjectMetadata;
          break;
        case 'listChangeRequestsOfLogisticsObjectById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningPaginatedChangeRequests;
          break;
        case 'getLogisticsObjectACLById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningACL;
          break;
        case 'getChangeRequestOnLogisticsObjectById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningChangeRequest;
          break;
        case 'getLogisticsObjectACLAuthorizationByMemberId':
        case 'upsertLogisticsObjectACLAuthorizationById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningACLAuthorization;
          break;
        case 'deleteLogisticsObjectACLAuthorizationByMemberId':
        case 'createLogisticsObjectEvent':
        case 'deleteLogisticsObjectEventById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningVoid;
          break;
        case 'createChangeRequestOnLogisticsObjectById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningChangeRequestOrNullOrUndefined;
          break;
        case 'updateChangeRequestOnLogisticsObjectById':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningChangeRequest;
          break;
        case 'getLogisticsObjectEvents':
          apiWithAliases[alias] = getApiMethod(this.api, aliasMap[alias]).bind(this.api) as ApiMethodReturningPaginatedEvents;
          break;
        default:
          {
            const unreachable: never = alias;
            throw new Error(`Unhandled logistics object alias "${unreachable}"`);
          }
      }
    });

    return apiWithAliases;
  }
}

/**
 * Singleton factory for BOL API clients
 */
class LogisticsObjectsApiFactory {
  private activitiesApi: ActivitiesApi | null = null;
  private consignmentsApi: ConsignmentsApi | null = null;
  private customerOrdersApi: CustomerOrdersApi | null = null;
  private humanMeansApi: HumanMeansApi | null = null;
  private invoicingDocumentsApi: InvoicingDocumentsApi | null = null;
  private itemsApi: ItemsApi | null = null;
  private materialMeansApi: MaterialMeansApi | null = null;
  private piecesApi: PiecesApi | null = null;
  private productsApi: ProductsApi | null = null;
  private quotationRequestsApi: QuotationRequestsApi | null = null;
  private quotationsApi: QuotationsApi | null = null;
  private serviceRequestsApi: ServiceRequestsApi | null = null;
  private servicesApi: ServicesApi | null = null;
  private hostsApi: HostsApi | null = null;
  private membersApi: MembersApi | null = null;

  private getConfiguration(): Configuration {
    return new Configuration({
      basePath: '/bol-api',
      accessToken: async () => {
        try {
          const token = await oAuthService.getAccessToken();
          return `Bearer ${token}`;
        } catch (error) {
          console.error('[bolApiClient] Failed to get access token:', error);
          throw error;
        }
      },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
  }

  getActivitiesApi(): ActivitiesApi {
    if (!this.activitiesApi) {
      this.activitiesApi = new LogisticsObjectApiAliaser(new LogisticsObjectsActivitiesApi(this.getConfiguration()), 'Activity').build();
    }
    return this.activitiesApi;
  }

  getConsignmentsApi(): ConsignmentsApi {
    if (!this.consignmentsApi) {
      this.consignmentsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsConsignmentsApi(this.getConfiguration()), 'Consignment').build();
    }
    return this.consignmentsApi;
  }

  getCustomerOrdersApi(): CustomerOrdersApi {
    if (!this.customerOrdersApi) {
      this.customerOrdersApi = new LogisticsObjectApiAliaser(new LogisticsObjectsCustomerOrdersApi(this.getConfiguration()), 'CustomerOrder').build();
    }
    return this.customerOrdersApi;
  }

  getHumanMeansApi(): HumanMeansApi {
    if (!this.humanMeansApi) {
      this.humanMeansApi = new LogisticsObjectApiAliaser(new LogisticsObjectsHumanMeansApi(this.getConfiguration()), 'HumanMean').build();
    }
    return this.humanMeansApi;
  }

  getInvoicingDocumentsApi(): InvoicingDocumentsApi {
    if (!this.invoicingDocumentsApi) {
      this.invoicingDocumentsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsInvoicingDocumentsApi(this.getConfiguration()), 'InvoicingDocument').build();
    }
    return this.invoicingDocumentsApi;
  }

  getItemsApi(): ItemsApi {
    if (!this.itemsApi) {
      this.itemsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsItemsApi(this.getConfiguration()), 'Item').build();
    }
    return this.itemsApi;
  }

  getMaterialMeansApi(): MaterialMeansApi {
    if (!this.materialMeansApi) {
      this.materialMeansApi = new LogisticsObjectApiAliaser(new LogisticsObjectsMaterialMeansApi(this.getConfiguration()), 'MaterialMean').build();
    }
    return this.materialMeansApi;
  }

  getPiecesApi(): PiecesApi {
    if (!this.piecesApi) {
      this.piecesApi = new LogisticsObjectApiAliaser(new LogisticsObjectsPiecesApi(this.getConfiguration()), 'Piece').build();
    }
    return this.piecesApi;
  }

  getProductsApi(): ProductsApi {
    if (!this.productsApi) {
      this.productsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsProductsApi(this.getConfiguration()), 'Product').build();
    }
    return this.productsApi;
  }

  getQuotationRequestsApi(): QuotationRequestsApi {
    if (!this.quotationRequestsApi) {
      this.quotationRequestsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsQuotationRequestsApi(this.getConfiguration()), 'QuotationRequest').build();
    }
    return this.quotationRequestsApi;
  }

  getQuotationsApi(): QuotationsApi {
    if (!this.quotationsApi) {
      this.quotationsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsQuotationsApi(this.getConfiguration()), 'Quotation').build();
    }
    return this.quotationsApi;
  }

  getServiceRequestsApi(): ServiceRequestsApi {
    if (!this.serviceRequestsApi) {
      this.serviceRequestsApi = new LogisticsObjectApiAliaser(new LogisticsObjectsServiceRequestsApi(this.getConfiguration()), 'ServiceRequest').build();
    }
    return this.serviceRequestsApi;
  }

  getServicesApi(): ServicesApi {
    if (!this.servicesApi) {
      this.servicesApi = new LogisticsObjectApiAliaser(new LogisticsObjectsServicesApi(this.getConfiguration()), 'Service').build();
    }
    return this.servicesApi;
  }

  getHostsApi(): HostsApi {
    if (!this.hostsApi) {
      this.hostsApi = new HostsApi(this.getConfiguration());
    }
    return this.hostsApi;
  }

  getMembersApi(): MembersApi {
    if (!this.membersApi) {
      this.membersApi = new MembersApi(this.getConfiguration());
    }
    return this.membersApi;
  }

  reset(): void {
    this.activitiesApi = null;
    this.consignmentsApi = null;
    this.customerOrdersApi = null;
    this.humanMeansApi = null;
    this.invoicingDocumentsApi = null;
    this.itemsApi = null;
    this.materialMeansApi = null;
    this.piecesApi = null;
    this.productsApi = null;
    this.quotationRequestsApi = null;
    this.quotationsApi = null;
    this.serviceRequestsApi = null;
    this.servicesApi = null;
    this.hostsApi = null;
    this.membersApi = null;
  }
}

const factory = new LogisticsObjectsApiFactory();

/**
 * Get singleton instance of ActivitiesApi
 * Automatically uses token from oAuthService
 */
export function getActivitiesApi(): ActivitiesApi {
  return factory.getActivitiesApi();
}

/**
 * Get singleton instance of ConsignmentsApi
 * Automatically uses token from oAuthService
 */
export function getConsignmentsApi(): ConsignmentsApi {
  return factory.getConsignmentsApi();
}

/**
 * Get singleton instance of CustomerOrdersApi
 * Automatically uses token from oAuthService
 */
export function getCustomerOrdersApi(): CustomerOrdersApi {
  return factory.getCustomerOrdersApi();
}

/**
 * Get singleton instance of HumanMeansApi
 * Automatically uses token from oAuthService
 */
export function getHumanMeansApi(): HumanMeansApi {
  return factory.getHumanMeansApi();
}

/**
 * Get singleton instance of InvoicingDocumentsApi
 * Automatically uses token from oAuthService
 */
export function getInvoicingDocumentsApi(): InvoicingDocumentsApi {
  return factory.getInvoicingDocumentsApi();
}

/**
 * Get singleton instance of ItemsApi
 * Automatically uses token from oAuthService
 */
export function getItemsApi(): ItemsApi {
  return factory.getItemsApi();
}

/**
 * Get singleton instance of MaterialMeansApi
 * Automatically uses token from oAuthService
 */
export function getMaterialMeansApi(): MaterialMeansApi {
  return factory.getMaterialMeansApi();
}

/**
 * Get singleton instance of PiecesApi
 * Automatically uses token from oAuthService
 */
export function getPiecesApi(): PiecesApi {
  return factory.getPiecesApi();
}

/**
 * Get singleton instance of ProductsApi
 * Automatically uses token from oAuthService
 */
export function getProductsApi(): ProductsApi {
  return factory.getProductsApi();
}

/**
 * Get singleton instance of QuotationRequestsApi
 * Automatically uses token from oAuthService
 */
export function getQuotationRequestsApi(): QuotationRequestsApi {
  return factory.getQuotationRequestsApi();
}

/**
 * Get singleton instance of QuotationsApi
 * Automatically uses token from oAuthService
 */
export function getQuotationsApi(): QuotationsApi {
  return factory.getQuotationsApi();
}

/**
 * Get singleton instance of ServiceRequestsApi
 * Automatically uses token from oAuthService
 */
export function getServiceRequestsApi(): ServiceRequestsApi {
  return factory.getServiceRequestsApi();
}

/**
 * Get singleton instance of ServicesApi
 * Automatically uses token from oAuthService
 */
export function getServicesApi(): ServicesApi {
  return factory.getServicesApi();
}

/**
 * Get singleton instance of HostsApi
 * Automatically uses token from oAuthService
 */
export function getHostsApi(): HostsApi {
  return factory.getHostsApi();
}

/**
 * Get singleton instance of MembersApi
 * Automatically uses token from oAuthService
 */
export function getMembersApi(): MembersApi {
  return factory.getMembersApi();
}

/**
 * Reset all API instances (useful after logout or token refresh)
 */
export function resetBolApiInstances(): void {
  factory.reset();
}

export function getLogisticsObjectsApi(logisticsObjectType: string): LogisticsObjectsApi {
  const normalizedType = normalizeLogisticsObjectType(logisticsObjectType);
  const typeNamePascal = getLogisticsObjectTypeTokens(normalizedType).singularPascal;
  const api = getApi(normalizedType);

  return new LogisticsObjectApiAliaser(api, typeNamePascal).build();
}
